/**
 * Marketing flyer export — A4 portrait poster.
 * PDF uses jsPDF with high-DPI embedded rasters.
 * Image export writes a lossless PNG at print resolution (300 DPI).
 */
import { API_BASE_URL } from '../api';

const PAGE = { w: 210, h: 297 }; // A4 portrait mm
const BEIGE = [232, 221, 212];
const PRINT_DPI = 300;

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function safeExportBaseName(payload = {}) {
  const {
    fileName: fileNameOpt,
    documentTitle,
    copy = {},
  } = payload;
  const productTitle = String(copy.productTitle || '').trim();
  return (
    String(fileNameOpt || documentTitle || productTitle || 'fabric-marketing')
      .trim()
      .replace(/\.(pdf|png|jpe?g)$/i, '')
      .replace(/[^\w\- ]+/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 64) || 'fabric-marketing'
  );
}

async function urlToDataUrl(url, timeoutMs = 45000) {
  if (!url) return null;
  const src = String(url).trim();
  if (!src) return null;
  if (src.startsWith('data:')) return src;

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    try {
      const proxyUrl = `${API_BASE_URL}/s3/proxy-image?url=${encodeURIComponent(src)}`;
      const proxyRes = await fetch(proxyUrl, {
        signal: controller.signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (proxyRes.ok) {
        const blob = await proxyRes.blob();
        if (blob?.size > 0) return await blobToDataUrl(blob);
      } else {
        console.warn('[kitMarketingExport] proxy status', proxyRes.status);
      }
    } catch (err) {
      console.warn('[kitMarketingExport] proxy error', err);
    }

    try {
      const directRes = await fetch(src, {
        mode: 'cors',
        credentials: 'omit',
        cache: 'force-cache',
        signal: controller.signal,
      });
      if (directRes.ok) {
        const blob = await directRes.blob();
        if (blob?.size > 0) return await blobToDataUrl(blob);
      }
    } catch (err) {
      console.warn('[kitMarketingExport] direct fetch error', err);
    }

    return null;
  } finally {
    clearTimeout(timer);
  }
}

function mmToPx(mm, dpi = PRINT_DPI) {
  return Math.round((mm / 25.4) * dpi);
}

function roundRectPath(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawContainImage(ctx, img, box, transform = {}, fit = 'contain') {
  const { x, y, w, h } = box;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.fillStyle = '#e8ddd4';
  ctx.fillRect(x, y, w, h);

  const nw = Math.max(1, img.naturalWidth);
  const nh = Math.max(1, img.naturalHeight);
  const imgAspect = nw / nh;
  const frameAspect = w / h;
  const cover = fit === 'cover';
  let drawW;
  let drawH;
  if (imgAspect > frameAspect) {
    if (cover) {
      drawH = h;
      drawW = h * imgAspect;
    } else {
      drawW = w;
      drawH = w / imgAspect;
    }
  } else if (cover) {
    drawW = w;
    drawH = w / imgAspect;
  } else {
    drawH = h;
    drawW = h * imgAspect;
  }
  const zoom = Math.max(1, Number(transform.scale) || 1);
  drawW *= zoom;
  drawH *= zoom;
  const px = Number.isFinite(transform.x) ? transform.x : 50;
  const py = Number.isFinite(transform.y) ? transform.y : 50;
  const dx = x + ((w - drawW) * px) / 100;
  const dy = y + ((h - drawH) * py) / 100;
  ctx.drawImage(img, dx, dy, drawW, drawH);
  ctx.restore();
}

async function loadExportAssets(payload = {}) {
  const {
    copy = {},
    fabricImageUrl = null,
    kitImageUrls = [],
    imageTransforms = {},
  } = payload;

  const fabricUrl = fabricImageUrl ? await urlToDataUrl(fabricImageUrl) : null;
  const kitUrls = [];
  for (const url of (kitImageUrls || []).slice(0, 3)) {
    // eslint-disable-next-line no-await-in-loop
    const data = await urlToDataUrl(url);
    if (data) kitUrls.push(data);
  }

  return {
    copy,
    imageTransforms,
    fabricUrl,
    kitUrls,
    fabricImg: fabricUrl ? await loadImage(fabricUrl) : null,
    kitImgs: await Promise.all(kitUrls.map((u) => loadImage(u))),
  };
}

function layoutPortraitBoxes(
  pageW,
  pageH,
  fontScale = 1,
  fabricSize = 1,
  fabricPos = null,
  detailsPos = null,
  detailsWidthPct = null
) {
  const contentH = pageH;
  const pad = 6;
  const sizeMul = Math.max(0.55, Math.min(2.2, Number(fabricSize) || 1));
  const swatchSize = 42 * sizeMul;
  const scale = Math.max(0.85, Math.min(1.75, Number(fontScale) || 1));
  // Provisional size — final width/height are measured from text when drawing
  const widthLocked =
    detailsWidthPct != null && Number.isFinite(Number(detailsWidthPct));
  const widthFrac = widthLocked
    ? Math.max(0.16, Math.min(0.9, Number(detailsWidthPct) / 100))
    : Math.min(0.55, 0.28 + (scale - 1) * 0.08);
  const detailsW = pageW * widthFrac;
  const detailsH = Math.min(pageH * 0.5, 48 + (scale - 1) * 24);
  const overlayBottom = 4;
  // Same defaults / clamp as KitMarketingFlyer normalizePos (0–92%)
  const DEFAULT_DETAILS = { x: 52, y: 62 };

  const kitBox = {
    x: 0,
    y: 0,
    w: pageW,
    h: contentH,
  };

  const defaultFabricX = pad;
  const defaultFabricY = contentH - overlayBottom - swatchSize;
  const fx =
    fabricPos && Number.isFinite(Number(fabricPos.x))
      ? (Number(fabricPos.x) / 100) * pageW
      : defaultFabricX;
  const fy =
    fabricPos && Number.isFinite(Number(fabricPos.y))
      ? (Number(fabricPos.y) / 100) * contentH
      : defaultFabricY;

  const fabricBox = {
    x: Math.max(0, Math.min(pageW - swatchSize, fx)),
    y: Math.max(0, Math.min(contentH - swatchSize, fy)),
    w: swatchSize,
    h: swatchSize,
  };

  // Preview uses left/top % of stage for the panel's top-left — do not
  // re-clamp against provisional width/height (that shifts the box on export).
  const dxPct =
    detailsPos && Number.isFinite(Number(detailsPos.x))
      ? Math.max(0, Math.min(92, Number(detailsPos.x)))
      : DEFAULT_DETAILS.x;
  const dyPct =
    detailsPos && Number.isFinite(Number(detailsPos.y))
      ? Math.max(0, Math.min(92, Number(detailsPos.y)))
      : DEFAULT_DETAILS.y;

  const detailsBox = {
    x: (dxPct / 100) * pageW,
    y: (dyPct / 100) * contentH,
    w: detailsW,
    h: detailsH,
    widthLocked,
  };

  return { contentH, pad, kitBox, fabricBox, detailsBox };
}

/** Wrap text to maxWidth without truncating (word + hard char breaks). */
function wrapCanvasText(ctx, text, maxWidth) {
  const raw = String(text || '');
  if (!raw) return [''];
  if (maxWidth <= 0) return [raw];
  const words = raw.split(/\s+/);
  const lines = [];
  let current = '';

  const pushHardBreak = (word) => {
    let chunk = '';
    for (const ch of word) {
      const next = chunk + ch;
      if (ctx.measureText(next).width > maxWidth && chunk) {
        lines.push(chunk);
        chunk = ch;
      } else {
        chunk = next;
      }
    }
    return chunk;
  };

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
      continue;
    }
    if (current) lines.push(current);
    if (ctx.measureText(word).width > maxWidth) {
      current = pushHardBreak(word);
    } else {
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

/**
 * Compose a full portrait poster to a canvas (lossless PNG-ready).
 * Kit is full-bleed; fabric swatch + details are overlays (reference layout).
 */
async function composePortraitPosterCanvas(payload = {}, dpi = PRINT_DPI) {
  const assets = await loadExportAssets(payload);
  const pageWmm = PAGE.w;
  const pageHmm = PAGE.h;
  const canvasW = mmToPx(pageWmm, dpi);
  const canvasH = mmToPx(pageHmm, dpi);
  const scale = canvasW / pageWmm;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const detailStyle = assets.copy.detailStyle || {};
  const fontScale = Math.max(0.85, Math.min(1.75, Number(detailStyle.fontScale) || 1));
  const detailColor =
    typeof detailStyle.color === 'string' && detailStyle.color.trim()
      ? detailStyle.color.trim()
      : '#ffffff';
  const fabricSize = Math.max(
    0.55,
    Math.min(2.2, Number(assets.imageTransforms?.fabricSize) || 1)
  );
  const fabricPos = assets.imageTransforms?.fabricPos || null;
  const detailsPos = assets.imageTransforms?.detailsPos || null;
  const detailsWidthPct = assets.imageTransforms?.detailsWidthPct;

  const { kitBox, fabricBox, detailsBox } = layoutPortraitBoxes(
    pageWmm,
    pageHmm,
    fontScale,
    fabricSize,
    fabricPos,
    detailsPos,
    detailsWidthPct
  );
  const sx = (mm) => mm * scale;

  // Full-bleed kit background
  ctx.fillStyle = `rgb(${BEIGE.join(',')})`;
  ctx.fillRect(0, 0, canvasW, canvasH);

  const detailRows = Array.isArray(assets.copy.detailRows)
    ? assets.copy.detailRows.filter((r) => r && (r.label || r.value))
    : [];

  // Kits (full bleed)
  const kitTransforms = assets.imageTransforms.kits || [];
  const gap = sx(1.5);
  const kBox = {
    x: sx(kitBox.x),
    y: sx(kitBox.y),
    w: sx(kitBox.w),
    h: sx(kitBox.h),
  };
  const imgs = assets.kitImgs;
  if (imgs.length === 1) {
    drawContainImage(ctx, imgs[0], kBox, kitTransforms[0], 'cover');
  } else if (imgs.length === 2) {
    const w = (kBox.w - gap) / 2;
    drawContainImage(ctx, imgs[0], { x: kBox.x, y: kBox.y, w, h: kBox.h }, kitTransforms[0], 'cover');
    drawContainImage(
      ctx,
      imgs[1],
      { x: kBox.x + w + gap, y: kBox.y, w, h: kBox.h },
      kitTransforms[1],
      'cover'
    );
  } else if (imgs.length >= 3) {
    const leftWKits = (kBox.w - gap) * 0.55;
    const rightWKits = kBox.w - gap - leftWKits;
    const halfH = (kBox.h - gap) / 2;
    drawContainImage(
      ctx,
      imgs[0],
      { x: kBox.x, y: kBox.y, w: leftWKits, h: kBox.h },
      kitTransforms[0],
      'cover'
    );
    drawContainImage(
      ctx,
      imgs[1],
      { x: kBox.x + leftWKits + gap, y: kBox.y, w: rightWKits, h: halfH },
      kitTransforms[1],
      'cover'
    );
    drawContainImage(
      ctx,
      imgs[2],
      {
        x: kBox.x + leftWKits + gap,
        y: kBox.y + halfH + gap,
        w: rightWKits,
        h: halfH,
      },
      kitTransforms[2],
      'cover'
    );
  }

  // Fabric swatch overlay (bottom-left)
  if (assets.fabricImg) {
    const box = {
      x: sx(fabricBox.x),
      y: sx(fabricBox.y),
      w: sx(fabricBox.w),
      h: sx(fabricBox.h),
    };
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.28)';
    ctx.shadowBlur = sx(2.5);
    ctx.shadowOffsetY = sx(1);
    ctx.fillStyle = `rgb(${BEIGE.join(',')})`;
    roundRectPath(ctx, box.x, box.y, box.w, box.h, sx(3));
    ctx.fill();
    ctx.restore();
    ctx.save();
    roundRectPath(ctx, box.x, box.y, box.w, box.h, sx(3));
    ctx.clip();
    drawContainImage(ctx, assets.fabricImg, box, assets.imageTransforms.fabric || {});
    ctx.restore();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = Math.max(1, sx(0.4));
    roundRectPath(ctx, box.x, box.y, box.w, box.h, sx(3));
    ctx.stroke();
  }

  // Detail rows overlay — size to content; wrap when width is constrained (never ellipsize)
  {
    const rows = detailRows.slice(0, 12);
    const padX = sx(2.8);
    const padY = sx(3.2);
    const fontPx = Math.max(20, Math.round(sx(2.6 * fontScale)));
    const lineH = Math.max(Math.round(fontPx * 1.25), Math.round(sx(3.2 * fontScale)));
    const labelFont = `800 ${fontPx}px Helvetica, Arial, sans-serif`;
    const valueFont = `700 ${fontPx}px Helvetica, Arial, sans-serif`;
    const rowFont = `700 ${fontPx}px Helvetica, Arial, sans-serif`;

    const rowTexts = rows.map((row) => {
      const label = String(row.label || '').trim().toUpperCase();
      const value = String(row.value || '').trim().toUpperCase();
      if (!label && !value) return '';
      return label ? `${label} - ${value}` : value;
    }).filter(Boolean);

    // Content-fit width unless user locked a width %
    let boxWmm = detailsBox.w;
    if (!detailsBox.widthLocked) {
      ctx.font = rowFont;
      let maxTextMm = 0;
      for (const text of rowTexts) {
        maxTextMm = Math.max(maxTextMm, ctx.measureText(text).width / scale);
      }
      const contentWmm = maxTextMm + (padX * 2) / scale + 2;
      const minW = pageWmm * 0.16;
      const maxW = pageWmm * 0.9;
      boxWmm = Math.max(minW, Math.min(maxW, contentWmm || detailsBox.w));
    }

    const maxTextW = sx(boxWmm) - padX * 2;
    ctx.font = rowFont;
    let totalLines = 0;
    const wrappedRows = rowTexts.map((text) => {
      const lines = wrapCanvasText(ctx, text, maxTextW);
      totalLines += lines.length;
      return lines;
    });
    if (totalLines === 0) totalLines = 1;

    const neededH = padY * 2 + totalLines * lineH + Math.max(0, wrappedRows.length - 1) * Math.round(lineH * 0.15);
    // Keep top-left at the same % as the editor preview — never shift after measuring size
    const box = {
      x: sx(detailsBox.x),
      y: sx(detailsBox.y),
      w: sx(Math.max(boxWmm, 28)),
      h: neededH,
    };

    const fillColor = detailColor;
    const isLightFill = (() => {
      const hex = String(fillColor).replace('#', '');
      if (hex.length !== 6) return /255|white|#fff/i.test(String(fillColor));
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 170;
    })();

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = Math.max(6, sx(1.4));
    ctx.shadowOffsetY = Math.max(2, sx(0.6));
    // Preview uses frosted glass; solid fill approximates that at print DPI
    ctx.fillStyle = isLightFill ? 'rgba(15, 23, 42, 0.82)' : 'rgba(255, 255, 255, 0.88)';
    roundRectPath(ctx, box.x, box.y, box.w, box.h, sx(2.2));
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = isLightFill ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.9)';
    ctx.lineWidth = Math.max(1, sx(0.25));
    roundRectPath(ctx, box.x, box.y, box.w, box.h, sx(2.2));
    ctx.stroke();

    const drawText = (text, x, y, font, fill) => {
      ctx.font = font;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillStyle = fill;
      ctx.fillText(text, x, y);
    };

    let dy = box.y + padY + lineH * 0.45;
    const textX = box.x + padX;
    const rowGap = Math.round(lineH * 0.15);

    wrappedRows.forEach((lines) => {
      lines.forEach((line) => {
        // Prefer bold label prefix when present
        const sep = ' - ';
        const sepIdx = line.indexOf(sep);
        if (sepIdx > 0) {
          const labelPart = line.slice(0, sepIdx + sep.length);
          const valuePart = line.slice(sepIdx + sep.length);
          drawText(labelPart, textX, dy, labelFont, fillColor);
          ctx.font = labelFont;
          const labelW = ctx.measureText(labelPart).width;
          drawText(valuePart, textX + labelW, dy, valueFont, fillColor);
        } else {
          drawText(line, textX, dy, valueFont, fillColor);
        }
        dy += lineH;
      });
      dy += rowGap;
    });
  }

  return {
    canvas,
    kitCount: imgs.length,
    hasFabric: Boolean(assets.fabricImg),
  };
}

/**
 * @param {object} payload
 * @param {object} payload.copy
 * @param {string|null} payload.fabricImageUrl
 * @param {string[]} payload.kitImageUrls
 * @param {{ fabric?: {scale,x,y}, fabricSize?: number, fabricPos?: {x,y}, detailsPos?: {x,y}, detailsWidthPct?: number|null, kits?: Array<{scale,x,y}> }} payload.imageTransforms
 * @param {string} [payload.documentTitle]
 * @param {string} [payload.fileName]
 */
export async function exportKitMarketingPdf(payload = {}) {
  if (payload instanceof HTMLElement || (payload && payload.nodeType === 1)) {
    throw new Error(
      'exportKitMarketingPdf now requires structured data { copy, fabricImageUrl, kitImageUrls }. Update the caller.'
    );
  }

  const [{ jsPDF }] = await Promise.all([import('jspdf')]);
  const { canvas, kitCount, hasFabric } = await composePortraitPosterCanvas(payload, PRINT_DPI);
  const pngDataUrl = canvas.toDataURL('image/png');

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });
  pdf.addImage(pngDataUrl, 'PNG', 0, 0, PAGE.w, PAGE.h, undefined, 'NONE');

  const base = safeExportBaseName(payload);
  const fileName = `${base}.pdf`;
  pdf.save(fileName);
  return { fileName, kitCount, hasFabric };
}

/**
 * Lossless PNG export at print DPI (composes kit + overlays via proxied images).
 */
export async function exportKitMarketingImage(payload = {}) {
  if (payload instanceof HTMLElement || (payload && payload.nodeType === 1)) {
    throw new Error(
      'exportKitMarketingImage requires structured data { copy, fabricImageUrl, kitImageUrls }.'
    );
  }

  const { canvas, kitCount, hasFabric } = await composePortraitPosterCanvas(payload, PRINT_DPI);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Failed to create PNG'))),
      'image/png'
    );
  });

  const base = safeExportBaseName(payload);
  const fileName = `${base}.png`;
  downloadBlob(blob, fileName);
  return {
    fileName,
    kitCount,
    hasFabric,
    width: canvas.width,
    height: canvas.height,
  };
}
